import React, { useState, useRef } from "react";

// Components
import AuthScreen from "./components/auth/AuthScreen";
import Sidebar from "./components/layout/Sidebar";
import BottomNav from "./components/layout/BottomNav";
import MoreSheet from "./components/layout/MoreSheet";
import PageHeader from "./components/layout/PageHeader";
import BrandMark from "./components/layout/BrandMark";
import { PAGE_TITLES } from "./components/layout/navItems";
import InfoBanner from "./components/common/InfoBanner";
import ErrorDisplay from "./components/common/ErrorDisplay";
import { ToastStack } from "./components/common/Toast";
import UserProfile from "./components/common/UserProfile";
import PropertiesContainer from "./components/property/PropertiesContainer";
import SearchesContainer from "./components/search/SearchesContainer";
import SyncHistoryContainer from "./components/syncHistory/SyncHistoryContainer";
import OpportunityDashboard from "./components/opportunities/OpportunityDashboard";
import AdminPanel from "./components/admin/AdminPanel";
import ImpersonationBanner from "./components/admin/ImpersonationBanner";
import AISettings from "./components/settings/AISettings";
import Button from "./components/ui/Button";
import { cn } from "./components/ui/cn";

// Hooks
import { useAuth } from "./hooks/useAuth";
import { useAdminAuth } from "./hooks/useAdminAuth";
import { useAppSettings } from "./hooks/useAppSettings";
import { useImpersonation } from "./hooks/useImpersonation";
import { useProperties } from "./hooks/useProperties";
import { useTrackedSearches } from "./hooks/useTrackedSearches";
import { useSyncHistory } from "./hooks/useSyncHistory";
import { useOpportunities } from "./hooks/useOpportunities";
import { useCacheConfig } from "./hooks/useCacheConfig";
import { useSidebarCollapsed } from "./hooks/useSidebarCollapsed";

// Services
import { analyzePropertyDescription, compareProperties } from "./services/geminiService";
import { getCachedResult, setCachedResult } from "./services/cacheService";
import { fetchPropertyData, detectPropertyType } from "./services/propertyService";
import { scanSearch } from "./services/searchScanService";
import { unwrapProxySearchUrl } from "./utils/propertyPlatforms";
import { findSimilarProperties } from "./utils/propertyUtils";
import { generateAnalysisHash, generateComparisonHash } from "./utils/hashUtils";
import { formatDate } from "./utils/dateUtils";
import { exportPropertiesToCSV, timeAgo } from "./utils/csvExport";

// Firebase
import { writeBatch, doc, serverTimestamp, deleteDoc, updateDoc } from "firebase/firestore";
import { db } from "./config/firebase";

function App() {
  // Auth hook
  const {
    currentUser,
    userId,
    userDisplayName,
    userEmail,
    isAuthLoading,
    isAuthReady,
    authError,
    handleGoogleSignIn,
    handleSignInAnonymously,
    handleSignOut,
  } = useAuth();

  // Admin auth hook
  const { isAdmin } = useAdminAuth(userId);

  // App settings hook
  const { infoBanner } = useAppSettings();

  // Impersonation hook (admin only)
  const {
    impersonatedUser,
    impersonatedUserId,
    isImpersonating,
    startImpersonation,
    stopImpersonation
  } = useImpersonation();

  // Effective user ID - use impersonated user if admin is impersonating
  const effectiveUserId = isAdmin && impersonatedUserId ? impersonatedUserId : userId;

  // Properties hook - uses effectiveUserId for impersonation support
  const {
    properties,
    isLoading: propertiesLoading,
    error: propertiesError,
    addProperty,
    updateProperty,
    deleteProperty,
    toggleFavorite,
    refetchAllProperties,
    refetchProperty,
  } = useProperties(effectiveUserId, isAuthReady);

  // Tracked searches hook - uses effectiveUserId for impersonation support
  const {
    trackedSearches,
    isLoading: searchesLoading,
    error: searchesError,
    addTrackedSearch,
  } = useTrackedSearches(effectiveUserId, isAuthReady);

  // Sync history hook - uses effectiveUserId for impersonation support
  const {
    syncHistory,
    isLoading: syncHistoryLoading,
    error: syncHistoryError,
    addSyncRecord,
    deleteSyncRecord,
    clearOldSyncHistory
  } = useSyncHistory(effectiveUserId, isAuthReady);

  // Opportunities hook
  const { opportunities } = useOpportunities(effectiveUserId, isAuthReady);

  // Cache config hook
  const { cacheConfig } = useCacheConfig();

  // Sidebar collapsed state (desktop, persisted)
  const { collapsed: sidebarCollapsed, toggle: toggleSidebar } = useSidebarCollapsed();

  // In-flight deduplication — prevents duplicate Gemini calls on rapid clicks
  const inFlightOps = useRef(new Set());

  // Local state
  const [currentPage, setCurrentPage] = useState("properties");
  const [moreOpen, setMoreOpen] = useState(false);
  const [scanningSearchId, setScanningSearchId] = useState(null);
  const [newlyFoundItems, setNewlyFoundItems] = useState({});
  const [isRefetchingAll, setIsRefetchingAll] = useState(false);
  const [refetchingPropertyId, setRefetchingPropertyId] = useState(null);
  const [refetchSummary, setRefetchSummary] = useState("");
  
  // Toast notifications
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Property analysis and comparison handlers
  const handleAnalyzeProperty = async (property) => {
    try {
      const opKey = `${property.id}_analysis`;

      if (inFlightOps.current.has(opKey)) return null;

      const { hash, inputLength } = generateAnalysisHash(property);
      const cached = await getCachedResult({
        userId: effectiveUserId, propertyId: property.id,
        type: 'analysis', hash, inputLength
      });
      if (cached) return cached;

      inFlightOps.current.add(opKey);

      try {
        const result = await analyzePropertyDescription(property);

        setCachedResult({
          userId: effectiveUserId, propertyId: property.id,
          type: 'analysis', hash, inputLength,
          resultMarkdown: result,
          ttlDays: cacheConfig.analysisCacheTTLDays
        });

        return result;
      } finally {
        inFlightOps.current.delete(opKey);
      }
    } catch (error) {
      console.error('Error analyzing property:', error);
      throw error;
    }
  };

  const handleCompareProperty = async (property, allProperties) => {
    try {
      const similarProperties = findSimilarProperties(property, allProperties);

      if (similarProperties.length === 0) {
        return '## No Similar Properties Found\n\nThere are no similar properties in your tracked list to compare against. Try tracking more properties with similar characteristics (price range, bedrooms, area).';
      }

      const opKey = `${property.id}_comparison`;

      if (inFlightOps.current.has(opKey)) return null;

      const { hash, inputLength } = generateComparisonHash(property, similarProperties);
      const cached = await getCachedResult({
        userId: effectiveUserId, propertyId: property.id,
        type: 'comparison', hash, inputLength
      });
      if (cached) return cached;

      inFlightOps.current.add(opKey);

      try {
        const result = await compareProperties(property, similarProperties);

        setCachedResult({
          userId: effectiveUserId, propertyId: property.id,
          type: 'comparison', hash, inputLength,
          resultMarkdown: result,
          ttlDays: cacheConfig.comparisonCacheTTLDays
        });

        return result;
      } finally {
        inFlightOps.current.delete(opKey);
      }
    } catch (error) {
      console.error('Error comparing property:', error);
      throw error;
    }
  };

  // Search scanning handler - platform-agnostic via searchScanService
  const handleScanSearch = async (search) => {
    if (!db || !userId) {
      console.error("Database or user not available.");
      return;
    }
    setScanningSearchId(search.id);
    setNewlyFoundItems((prev) => ({ ...prev, [search.id]: [] }));

    try {
      // Delegate to the unified scan service (handles Bazaraki, BuySellCyprus, etc.)
      const targetUrl = unwrapProxySearchUrl(search.searchUrl);
      const items = await scanSearch(targetUrl);

      const currentItemIds = items
        .map((item) => String(item.id))
        .filter((id) => id && id !== "undefined" && id !== "null");

      const previouslySeenIds = new Set(search.lastSeenItemIds || []);
      const newItemsFromScan = items.filter(
        (item) => !previouslySeenIds.has(String(item.id))
      );

      setNewlyFoundItems((prev) => ({
        ...prev,
        [search.id]: newItemsFromScan,
      }));

      const batch = writeBatch(db);
      const searchDocRef = doc(
        db,
        `artifacts/bz-tracker/users/${userId}/trackedSearches`,
        search.id
      );
      batch.update(searchDocRef, {
        lastScanTimestamp: serverTimestamp(),
        lastSeenItemIds: currentItemIds,
      });
      await batch.commit();
    } catch (err) {
      console.error(
        `Error scanning search "${search.name}" URL "${search.searchUrl}":`,
        err
      );
      addToast(`Failed to scan "${search.name}": ${err.message}`, 'error');
    } finally {
      setScanningSearchId(null);
    }
  };

  // Toggle opportunity tracking for a search
  const handleToggleOpportunityTracking = async (searchId, enabled) => {
    if (!db || !userId) {
      console.error("Database or user not available.");
      return;
    }
    try {
      const searchDocRef = doc(
        db,
        `artifacts/bz-tracker/users/${userId}/trackedSearches`,
        searchId
      );
      await updateDoc(searchDocRef, {
        'opportunityTracking.enabled': enabled,
        'opportunityTracking.lastUpdated': serverTimestamp(),
      });
      addToast(
        enabled 
          ? '✅ Opportunity tracking enabled!' 
          : 'ℹ️ Opportunity tracking disabled',
        enabled ? 'success' : 'info'
      );
    } catch (err) {
      console.error("Error toggling opportunity tracking:", err);
      addToast('❌ Failed to update opportunity tracking', 'error');
    }
  };

  // Get opportunity stats for a specific search
  const getOpportunityStatsForSearch = (searchId) => {
    const searchOpportunities = opportunities.filter(
      opp => opp.searchId === searchId && opp.status === 'active'
    );
    
    if (searchOpportunities.length === 0) return null;
    
    return {
      active: searchOpportunities.length,
      lastScan: searchOpportunities[0]?.detectedAt || null,
    };
  };

  // Delete tracked search handler
  const handleDeleteTrackedSearch = async (searchId) => {
    if (!db || !userId) {
      console.error("Data missing for delete.");
      return;
    }
    try {
      await deleteDoc(
        doc(db, `artifacts/bz-tracker/users/${userId}/trackedSearches`, searchId)
      );
      setNewlyFoundItems((prev) => {
        const newState = { ...prev };
        delete newState[searchId];
        return newState;
      });
    } catch (err) {
      console.error("Error deleting tracked search:", err);
    }
  };

  // Track item from search results - uses normalized data from fetchPropertyData
  const handleTrackItem = async (itemUrl) => {
    try {
      // fetchPropertyData returns normalized data (same shape for all platforms)
      const data = await fetchPropertyData(itemUrl);
      
      if (data) {
        const { forSale, forRent } = detectPropertyType(itemUrl, data);
        
        const propertyData = {
          url: itemUrl,
          price: parseFloat(data.price || "0"),
          publishedDate: data.publishedDate || "",
          updatedDate: data.updatedDate || "",
          notes: data.title ? `Title: ${data.title}\n${data.description || ""}` : "",
          forSale,
          forRent,
          additionalFields: {
            title: data.title || "",
            area: data.area || "",
            bedrooms: data.bedrooms || "",
            bathrooms: data.bathrooms || "",
            startPrice: data.startPrice || "",
            imageURL: data.imageURL || "",
            latitude: data.latitude || null,
            longitude: data.longitude || null,
            pets: data.pets || "",
            furnishing: data.furnishing || "",
            parking: data.parking || "",
            constructionYear: data.constructionYear || "",
            onlineViewing: data.onlineViewing || "",
            landlordName: data.landlordName || "",
            landlordJoined: data.landlordJoined || "",
            ownerAdCount: data.ownerAdCount || "",
            cityId: data.cityId || "",
            // Distressed-asset signals (vendor/auction/bank-owned badges)
            currency: data.currency || "EUR",
            priceOnRequest: data.priceOnRequest || false,
            sellerType: data.sellerType || "unknown",
            distress: data.distress || null,
            isDistressed: data.isDistressed || false,
          }
        };
        
        const result = await addProperty(propertyData);
        
        if (result.isNew) {
          addToast('✅ Property tracked successfully!', 'success');
        } else if (result.wasDeleted) {
          addToast('✅ Property restored and tracked!', 'success');
        } else if (result.hasUpdate) {
          addToast('✅ Property updated with new information!', 'success');
        } else {
          addToast('ℹ️ Property already exists with same information', 'info');
        }
        
        return result;
      } else {
        throw new Error('Failed to fetch property data');
      }
    } catch (error) {
      console.error('Error tracking item from search:', error);
      addToast(`❌ Failed to track property: ${error.message}`, 'error');
      throw error;
    }
  };

  // Refetch all properties handler
  const handleRefetchAll = async () => {
    setIsRefetchingAll(true);
    setRefetchSummary("");
    
    try {
      const result = await refetchAllProperties(fetchPropertyData);
      setRefetchSummary(result.summary || "All properties refreshed successfully");
      
      // Save sync history if there were changes or errors
      if (result && result.syncData) {
        try {
          await addSyncRecord(result.syncData);
        } catch (syncError) {
          console.error("Error saving sync history:", syncError);
        }
      }
    } catch (error) {
      console.error('Error refetching properties:', error);
      setRefetchSummary(`Error: ${error.message}`);
    } finally {
      setIsRefetchingAll(false);
    }
  };

  const handleRefetchProperty = async (propertyId) => {
    setRefetchingPropertyId(propertyId);
    try {
      const result = await refetchProperty(propertyId, fetchPropertyData);
      if (result.updated && result.changes.length > 0) {
        addToast(`Updated: ${result.changes.join(', ')}`, 'success');
      } else {
        addToast('Property data is up to date', 'info');
      }
    } catch (error) {
      console.error('Error refetching property:', error);
      addToast(`Re-fetch failed: ${error.message}`, 'error');
    } finally {
      setRefetchingPropertyId(null);
    }
  };

  // Export every non-deleted property (used from the More sheet)
  const handleExportAllCSV = () => {
    const rows = properties.filter((p) => !p.deleted);
    if (!exportPropertiesToCSV(rows)) {
      addToast('No properties to export', 'info');
    }
  };

  // Show loading screen while auth is initializing
  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-4">
          <BrandMark size={72} />
          <div className="text-lg font-semibold text-primary tracking-tight">BZ-Tracker</div>
          <div className="w-28 h-1 rounded-full bg-line overflow-hidden">
            <div className="h-full w-1/2 bg-accent rounded-full animate-[slide-in-right_1.2s_ease-in-out_infinite]" />
          </div>
        </div>
      </div>
    );
  }

  // Show auth screen if user is not authenticated
  if (!currentUser) {
    return (
      <AuthScreen
        onGoogleSignIn={handleGoogleSignIn}
        onAnonymousSignIn={handleSignInAnonymously}
        authError={authError}
        isLoading={isAuthLoading}
      />
    );
  }

  // Header counts
  const liveProperties = properties.filter(p => !p.deleted && Number(p.lastKnownPrice) !== 0);
  const soldCount = properties.filter(p => !p.deleted && Number(p.lastKnownPrice) === 0).length;
  const favoriteCount = liveProperties.filter(p => p.isFavorite).length;
  const activeOpportunitiesCount = opportunities.filter(o => o.status === 'active').length;
  const lastSync = syncHistory?.[0]?.timestamp;

  const subtitles = {
    properties: `${liveProperties.length} tracked · ${favoriteCount} favorite${favoriteCount === 1 ? '' : 's'}${soldCount > 0 ? ` · ${soldCount} sold hidden` : ''}`,
    searches: `${trackedSearches?.length || 0} active`,
    opportunities: `${activeOpportunitiesCount} active`,
    'sync-history': lastSync ? `Last sync ${timeAgo(lastSync)}` : 'No syncs yet',
    'ai-settings': 'Gemini configuration',
    admin: 'Users, policies and cache',
  };

  const navigate = (page) => {
    setCurrentPage(page);
    setMoreOpen(false);
  };

  return (
    <div className="min-h-screen bg-page text-primary font-sans">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <ImpersonationBanner
          impersonatedUser={impersonatedUser}
          onStopImpersonation={stopImpersonation}
        />
      )}

      {/* Desktop sidebar */}
      <Sidebar
        currentPage={currentPage}
        onNavigate={navigate}
        opportunityCount={activeOpportunitiesCount}
        isAdmin={isAdmin}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebar}
        userDisplayName={userDisplayName}
        userEmail={userEmail}
        onSignOut={handleSignOut}
      />

      {/* Mobile bottom navigation */}
      <BottomNav
        currentPage={currentPage}
        onNavigate={navigate}
        onOpenMore={() => setMoreOpen(true)}
        opportunityCount={activeOpportunitiesCount}
        moreOpen={moreOpen}
      />
      <MoreSheet
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        onNavigate={navigate}
        isAdmin={isAdmin}
        userDisplayName={userDisplayName}
        userEmail={userEmail}
        onSignOut={handleSignOut}
        onExportCSV={handleExportAllCSV}
        onRefetchAll={handleRefetchAll}
        isRefetchingAll={isRefetchingAll}
        lastSyncLabel={lastSync ? `Last sync ${timeAgo(lastSync)}` : undefined}
      />
      
      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen transition-[padding] duration-200 pb-[calc(64px+env(safe-area-inset-bottom)+1.5rem)] lg:pb-10',
          sidebarCollapsed ? 'lg:pl-rail' : 'lg:pl-sidebar',
          isImpersonating && 'pt-12'
        )}
      >
        <div className="max-w-[1400px] mx-auto px-4 pt-4 sm:px-6 lg:px-8 lg:pt-8">
          <PageHeader title={PAGE_TITLES[currentPage] || 'BZ-Tracker'} subtitle={subtitles[currentPage]}>
            <div className="lg:hidden">
              <UserProfile
                userDisplayName={userDisplayName}
                userEmail={userEmail}
                favoriteCount={favoriteCount}
                onSignOut={handleSignOut}
              />
            </div>
          </PageHeader>

          {currentUser && currentUser.isAnonymous && (
            <div className="mb-4 lg:mb-6 p-3.5 bg-card border border-accent/30 rounded-card flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-sm text-secondary flex-1">
                You're browsing anonymously. Sign in with Google to keep your properties and searches permanently.
              </p>
              <Button variant="primary" size="sm" onClick={handleGoogleSignIn}>
                Sign in with Google
              </Button>
            </div>
          )}

          <InfoBanner settings={infoBanner} />
          
          {/* General Error Display */}
          <ErrorDisplay error={authError} />

          {/* Page Content */}
          {currentPage === "properties" && (
            <PropertiesContainer
              properties={properties}
              isLoading={propertiesLoading}
              error={propertiesError}
              onAddProperty={addProperty}
              onUpdateProperty={updateProperty}
              onDeleteProperty={deleteProperty}
              onToggleFavorite={toggleFavorite}
              onRefetchAll={handleRefetchAll}
              onRefetchProperty={handleRefetchProperty}
              onAnalyzeProperty={handleAnalyzeProperty}
              onCompareProperty={handleCompareProperty}
              isRefetchingAll={isRefetchingAll}
              refetchingPropertyId={refetchingPropertyId}
              refetchSummary={refetchSummary}
              addToast={addToast}
            />
          )}

          {currentPage === "searches" && (
            <SearchesContainer
              trackedSearches={trackedSearches}
              isLoading={searchesLoading}
              error={searchesError}
              onAddSearch={addTrackedSearch}
              onDeleteSearch={handleDeleteTrackedSearch}
              onScanSearch={handleScanSearch}
              scanningSearchId={scanningSearchId}
              newlyFoundItems={newlyFoundItems}
              onTrackItem={handleTrackItem}
              formatDate={formatDate}
              addToast={addToast}
              onToggleOpportunityTracking={handleToggleOpportunityTracking}
              getOpportunityStatsForSearch={getOpportunityStatsForSearch}
              onNavigateToOpportunities={() => navigate('opportunities')}
            />
          )}

          {currentPage === "opportunities" && (
            <OpportunityDashboard />
          )}

          {currentPage === "sync-history" && (
            <SyncHistoryContainer
              syncHistory={syncHistory}
              isLoading={syncHistoryLoading}
              error={syncHistoryError}
              onDeleteSync={deleteSyncRecord}
              onClearOldHistory={clearOldSyncHistory}
              addToast={addToast}
            />
          )}

          {currentPage === "ai-settings" && (
            <div className="max-w-2xl">
              <AISettings userId={userId} />
            </div>
          )}

          {currentPage === "admin" && isAdmin && (
            <AdminPanel
              userId={userId}
              isAdmin={isAdmin}
              onImpersonate={startImpersonation}
            />
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <ToastStack toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

export default App;
